import { COBROWSING_FORM_ID_PREFIX, CobrowsingService } from '@agent/core/cobrowsing/cobrowsing.service';
import { CobrowsingMessageKey, CobrowsingSessionState, ToolName } from '@agent/core/store/cobrowsing';
import { CobrowsingAction } from '@agent/core/store/cobrowsing/cobrowsing.dispatcher';
import { CobrowsingFacade } from '@agent/core/store/cobrowsing/cobrowsing.facade';
import { Component, ElementRef, Inject, InjectionToken, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';

import { MatStepper } from '@angular/material/stepper';
import { Actions, ofType } from '@ngrx/effects';
import { filter, pairwise, tap } from 'rxjs';

import { AppConf, FormFacade, Formly, isNotNullOrUndefined, Logger, UntilDestroy, untilDestroyed } from '@contact/upgrade';

enum CobrowsingFormField {
  CODE = 'CODE',
}
const COBROWSING_FORMLY = new InjectionToken<Formly>('@contact-view/phone/cobrowsing');
const COBROWSING_DEFAULT_MAX_ACTIVE_SESSIONS = 3;

/**
 * Component managing everything pertaining to cobrowsing with the cobrowse.io framework.
 * Handles all interaction with cobrowse.io sdk
 * and manages the cobrowsing iframe using the external application service (required to be able to end sessions even if this component is destroyed)
 *
 */
@Component({
  selector: 'cng-phone-cobrowsing-workspace',
  templateUrl: './phone-cobrowsing-workspace.component.html',
  styleUrls: ['./phone-cobrowsing-workspace.component.scss'],
  providers: [
    {
      provide: COBROWSING_FORMLY,
      useClass: Formly,
    },
  ],
})
@UntilDestroy()
export class PhoneCobrowsingWorkspaceComponent implements OnInit, OnDestroy {
  @Input()
  public responseId: string;

  public readonly FormField = CobrowsingFormField;
  public readonly ToolName = ToolName;
  public form = this.formly.form;

  public errorMessageLabelKey?: CobrowsingMessageKey;
  public infoMessageLabelKey?: CobrowsingMessageKey;
  public isCobrowsingInProgress = false;
  private shouldCheckActiveSessions = true;
  // this variable is undefined until the checkIfMaxSessionsNumberIsReached is completed (ngOnInit). The stepper is hidden while it is undefined.
  public maxSessionsReached?: boolean = undefined;
  public errorOccurredDuringSessionApiCall = false;

  @ViewChild('stepper')
  public stepper: MatStepper;
  public initialStep = 0;

  @ViewChild('cobrowseContainer')
  public set cobrowseContainer(elt: ElementRef) {
    // needed because when reopening the component when a cobrowse session is on the attachment of the frame is
    // done before the DOM is rendered, so the container size cannot be calculated -> the frame is not displayed
    this._cobrowseContainer = elt;
    this.cobrowsingService.displayExternalApp(this.responseId, this._cobrowseContainer);
  }
  private _cobrowseContainer: ElementRef;

  public currentSessionCode = '';
  private currentSessionId?: string;
  private maxActiveSessions?: number;
  public isLaserToolSelected = true;
  public isClosedByCsr = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    @Inject(COBROWSING_FORMLY) private readonly formly: Formly,
    private readonly logger: Logger,
    private readonly appConf: AppConf,
    private readonly formFacade: FormFacade,
    private readonly cobrowsingService: CobrowsingService,
    private readonly cobrowsingFacade: CobrowsingFacade,
    private readonly actions$: Actions
  ) {
    this.logger = logger.forSource(PhoneCobrowsingWorkspaceComponent.name);
    this.appConf.pipe(untilDestroyed(this)).subscribe(({ cobrowsing }) => {
      this.maxActiveSessions = cobrowsing?.maxActiveSessions || COBROWSING_DEFAULT_MAX_ACTIVE_SESSIONS;
    });
  }

  ngOnInit() {
    // we add the booking id the the form ID to ensure the code will not be filled in with another call, in case the cleaning is badly done
    this.formly.setId([COBROWSING_FORM_ID_PREFIX, this.responseId]);
    this.initFormly();

    this.cobrowsingFacade
      .getByBookingId(this.responseId)
      .pipe(
        isNotNullOrUndefined(),
        filter(cobrowsingInfo => cobrowsingInfo.bookingId === this.responseId),
        untilDestroyed(this),
        tap(cobrowsingInfo => {
          this.currentSessionCode = '';
          const isCobrowsingInitiated =
            !!cobrowsingInfo.sessionId &&
            (!this.currentSessionId || cobrowsingInfo.sessionId === this.currentSessionId) &&
            cobrowsingInfo.sessionState !== CobrowsingSessionState.ended;
          if (isCobrowsingInitiated) {
            // we consider the cobrowsing as "initiated" (ie not necessarily ongoing, can be in error)
            // if there is a session id in the store corresponding to the current booking, and the session is in "active" state,
            this.openStepTwo(CobrowsingMessageKey.PENDING);
            if (!this.currentSessionId) {
              // we start the cobrowsing if the cobrowsing is initiated AND there is not already a session going on
              this.currentSessionId = cobrowsingInfo.sessionId;
              this.cobrowsingService.createCobrowsingFrame(cobrowsingInfo, this._cobrowseContainer);
            }
            this.currentSessionCode = cobrowsingInfo.sessionCode || '';
            if (cobrowsingInfo.sessionState === CobrowsingSessionState.active) this.setTool(ToolName.laser);
          }

          this.isCobrowsingInProgress = cobrowsingInfo.sessionState === CobrowsingSessionState.active && !cobrowsingInfo.errorMessageKey;
          this.shouldCheckActiveSessions = cobrowsingInfo.sessionState === CobrowsingSessionState.active;

          if (cobrowsingInfo.sessionState !== CobrowsingSessionState.ended) this.currentSessionId = undefined;
        }),
        pairwise()
      )
      .subscribe(([previousCobrowsingInfo, cobrowsingInfo]) => {
        if (cobrowsingInfo.sessionState === CobrowsingSessionState.ended) {
          if (!this.isClosedByCsr) {
            // for reporting cobrowse
            this.cobrowsingFacade.cobrowsingEnded(this.responseId);
            if (previousCobrowsingInfo.sessionState === CobrowsingSessionState.authorizing) {
              this.infoMessageLabelKey = CobrowsingMessageKey.REFUSED_BY_CONTACT;
              this.cobrowsingService.setCobrowsingEndStatus(this.responseId, CobrowsingMessageKey.REFUSED_BY_CONTACT, true);
            } else {
              this.infoMessageLabelKey = CobrowsingMessageKey.CLOSED_BY_CONTACT;
              this.cobrowsingService.setCobrowsingEndStatus(this.responseId, CobrowsingMessageKey.CLOSED_BY_CONTACT, true);
            }
          }

          this.form.reset();
        }
        this.errorMessageLabelKey = cobrowsingInfo.errorMessageKey;
      });

    // listening to errors when trying to start the cobrowsing
    this.actions$
      .pipe(
        ofType(CobrowsingAction.StartCobrowsingError),
        untilDestroyed(this),
        filter(({ info }) => info.bookingId === this.responseId)
      )
      .subscribe(({ info }) => (this.errorMessageLabelKey = info.errorMessageKey));

    this.checkIfMaxSessionsNumberIsReached();
  }

  ngOnDestroy() {
    // the frame cannot be destroyed with the component because it is needed to listen to external events
    // (i.e. the customer closing the session) so we only hide it when destroying the component
    this.cobrowsingService.hideExternalApp(this.responseId);
  }

  /**
   * This method checks if, when no cobrowsing is in progress, there are available sessions.
   * Otherwise, it "locks" the cobrowsing tab.
   */
  async checkIfMaxSessionsNumberIsReached(retrySession?: boolean) {
    if (this.shouldCheckActiveSessions) {
      this.maxSessionsReached = false;
    } else {
      await this.cobrowsingService
        .getActiveSessionsNumber(this.responseId)
        .then(sessionApiResult => {
          if (sessionApiResult.errorMessageKey) {
            this.openStepTwo(undefined, sessionApiResult.errorMessageKey);
          } else {
            if (this.maxActiveSessions && sessionApiResult.sessionNumber >= this.maxActiveSessions) {
              this.logger.error(
                `The maximum number of cobrowsing active sessions (${this.maxActiveSessions}) is reached. New sessions can't be opened right now.`
              );
              this.maxSessionsReached = true;
              this.openStepTwo(CobrowsingMessageKey.NO_MORE_SESSIONS_AVAILABLE);
            } else {
              this.maxSessionsReached = false;
              if (retrySession) this.openStepOne();
            }
          }
        })
        .catch(error => {
          this.maxSessionsReached = false;
          // This value is not reset to false anywhere cause the user is stuck on the "step 2" screen when an error occurs (specs)
          this.errorOccurredDuringSessionApiCall = true;
          this.logger.error(error);
        });
    }
    return this.maxSessionsReached;
  }

  initFormly() {
    this.formly.init({
      [CobrowsingFormField.CODE]: new FormControl('', [Validators.pattern('^\\d{6}$')]),
    });
  }

  /**
   * Fetches the session based on the code and uses it to start the cobrowsing
   * @async
   */
  async validateCode() {
    await this.checkIfMaxSessionsNumberIsReached();
    if (!this.maxSessionsReached) {
      this.openStepTwo(CobrowsingMessageKey.PENDING);
      this.cobrowsingFacade.startCobrowsing({ bookingId: this.responseId, sessionCode: this.form.value[CobrowsingFormField.CODE] });
    }
  }

  /**
   * Regroups modifications to template-related variables to display "step 2" of the cobrowsing cinematics
   */
  openStepTwo(infoMessageLabelKey?: CobrowsingMessageKey, errorMessageLabelKey?: CobrowsingMessageKey) {
    this.infoMessageLabelKey = infoMessageLabelKey || undefined;
    this.errorMessageLabelKey = errorMessageLabelKey || undefined;
    if (this.stepper) {
      this.stepper.next();
    } else {
      this.initialStep = 1;
    }
  }

  openStepOne() {
    this.infoMessageLabelKey = undefined;
    this.errorMessageLabelKey = undefined;
    this.stepper.previous();
  }

  /**
   * Ends the current cobrowsing session if there's one and returns to the first step
   * @private
   */
  closeSession(infoMessageKey: boolean) {
    this.isClosedByCsr = true;
    if (!this.cobrowsingService.isSent) {
      this.cobrowsingFacade.endCobrowsing(this.responseId);
      if (infoMessageKey) {
        this.cobrowsingService.setCobrowsingEndStatus(this.responseId, CobrowsingMessageKey.REFUSED_BY_CSR, true);
      } else {
        this.cobrowsingService.setCobrowsingEndStatus(this.responseId, CobrowsingMessageKey.CLOSED_BY_CSR, true);
      }
    }

    this.openStepOne();
  }

  /**
   *  Set the tool to use in the Agent session draw/laser.
   */
  setTool(toolName: ToolName) {
    const toolWasSet = this.cobrowsingService.setTool(this.responseId, toolName);
    if (toolWasSet) this.isLaserToolSelected = toolName === ToolName.laser;
  }

  /**
   * Clear all agent session annotations from the user's view.
   */
  clearAnnotations() {
    this.cobrowsingService.clearAnnotations(this.responseId);
  }
}
